import React from 'react';
import {
  AGENT_CONSENT_AUTHORIZATION_TO_DISCLOSE,
  renderAgentConsent,
  type AgentConsentVars,
} from '@usacsmideo/types';

/**
 * Shared patient-side consent block for the ECN agent invite (CU-86ajbm1e6, P2.2).
 *
 * Renders the CPO-approved P1 authorization template
 * (`AGENT_CONSENT_AUTHORIZATION_TO_DISCLOSE`) + the required affirmation checkbox,
 * used by BOTH invite sites (`PatientFamilyView` HCA path and
 * `AlternateAgentsManager` alternate path — the latter also mounted on the
 * careTeam alternateAgents screen). Extracting it here kills the inline copy
 * duplication that the whole ECN copy-finalization effort exists to prevent: the
 * substituted text == the hashed/signed template by construction, because there is
 * ONE `renderAgentConsent` call site in the portal. The mobile twin (P2.3) mirrors
 * this component.
 *
 * ── Consent-copy invariants (do not "fix" these — they are the signed copy) ──
 * - The template `{agentFirstName}` slot carries the FULL agent name string (the
 *   chart stores a single `agent` name, no split first/last). Grammatically
 *   correct in every P1 paragraph, and mirrors the backend accept page, so the
 *   patient- and agent-side records render the same party consistently. The var
 *   name is a slight misnomer inherited from the shared types contract.
 * - The checkbox label is EXACTLY "I agree to the terms and conditions" and the
 *   caller's send control must read "Send Invite" — the signed consent's final
 *   paragraph names both verbatim ('By selecting "I agree to the terms and
 *   conditions" below … clicking "Send Invite" below constitutes my electronic
 *   signature'). A mismatch is an e-signature integrity defect.
 *
 * ── CA CMIA 14-point requirement ──
 * The consent paragraphs and the affirmation label render at ≥14pt (14pt =
 * 18.667px ≈ 1.1667rem — just above Tailwind `text-lg`/18px), visually separated
 * in a bordered panel. The caller owns the arm/cancel affordances; this component
 * owns the consent text + its affirmation control only.
 */

/** Exact affirmation label, dictated by P1 paragraph 4. Do not paraphrase. */
export const CONSENT_AFFIRMATION_LABEL = 'I agree to the terms and conditions';

/** ≥14pt (CA CMIA) — 14pt = 18.667px. Applied to the consent text + label. */
const CONSENT_TEXT_CLASS = 'text-[1.1667rem] leading-relaxed';

export interface AgentConsentBlockProps {
  /**
   * The substitution vars for the P1 template. `agentFirstName` carries the full
   * agent name string (see invariants above); `patientFirstName` is required by
   * the type but unused by P1 (pass the real value regardless).
   */
  vars: AgentConsentVars;
  /** Whether the patient has ticked the affirmation checkbox. */
  affirmed: boolean;
  /** Called with the new checked state when the patient toggles the checkbox. */
  onAffirmChange: (_checked: boolean) => void;
  /** Disable the checkbox while a send is in flight. */
  disabled?: boolean;
}

/**
 * The rendered consent paragraphs + affirmation checkbox. Substitution is done
 * ONCE via `renderAgentConsent`; the caller passes only the vars + affirmation
 * control. Presentation (portal `<p>` blocks) lives here; the text does not.
 */
export function AgentConsentBlock({
  vars,
  affirmed,
  onAffirmChange,
  disabled,
}: AgentConsentBlockProps) {
  const paragraphs = React.useMemo(
    () => renderAgentConsent(AGENT_CONSENT_AUTHORIZATION_TO_DISCLOSE, vars),
    [vars],
  );

  return (
    <div className="flex flex-col gap-3 rounded-md border border-sky-200 bg-sky-50 p-4">
      {/* The signed authorization text (P1), ≥14pt, visually separated. */}
      <div className="flex flex-col gap-3 text-left text-gray-800">
        {paragraphs.map((paragraph, i) => (
          <p key={i} className={CONSENT_TEXT_CLASS}>
            {paragraph}
          </p>
        ))}
      </div>

      {/* The required affirmation. Label is the verbatim P1 wording. */}
      <label className={`flex items-start gap-2 text-left text-gray-700 ${CONSENT_TEXT_CLASS}`}>
        <input
          type="checkbox"
          checked={affirmed}
          disabled={disabled}
          onChange={(e) => onAffirmChange(e.target.checked)}
          className="mt-1.5"
        />
        <span>{CONSENT_AFFIRMATION_LABEL}</span>
      </label>
    </div>
  );
}

export default AgentConsentBlock;
